import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Monitor,
  MapPin,
  LayoutGrid,
  List,
  MoreVertical,
  Search,
  ChevronRight,
  Info,
  X,
} from "lucide-react";
import { Dropdown, Modal } from "antd";
import Animations from "../../animations/Animations";
import EmptyState from "../EmptyState";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const PAGE_SIZE = 10;

// Neutral identity hues only — deliberately excludes red/orange/amber (error,
// warning) and green (online/success) so a location's tile color can never be
// mistaken for a status signal.
const ICON_PALETTE = [
  { bg: "#eff6ff", color: "#3b82f6" }, // blue
  { bg: "#eef2ff", color: "#6366f1" }, // indigo
  { bg: "#f5f3ff", color: "#8b5cf6" }, // violet
  { bg: "#fdf4ff", color: "#a855f7" }, // purple
  { bg: "#ecfeff", color: "#06b6d4" }, // cyan
  { bg: "#fdf2f8", color: "#ec4899" }, // pink
];

const StatCard = ({ label, value, dot, dotColor, icon }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
    {icon && (
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
        {icon.el}
      </div>
    )}
    <div>
      <div
        style={{
          fontSize: 12,
          color: "#94a3b8",
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: "#0f172a",
          lineHeight: 1.2,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {dot && (
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: dotColor,
              display: "inline-block",
              flexShrink: 0,
            }}
          />
        )}
        {value}
      </div>
    </div>
  </div>
);

const StatusBadge = ({ offlineCount, screenCount, t, compact = false }) => {
  if (screenCount === 0) {
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 700,
          background: "#f1f5f9",
          color: "#94a3b8",
        }}
      >
        {t.statusNoScreens}
      </span>
    );
  }
  if (offlineCount > 0) {
    return (
      <div>
        <span
          style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            background: "#fff7ed",
            color: "#f97316",
          }}
        >
          {t.statusWarning}
        </span>
        {!compact && (
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
            {t.statusWarningDetail(offlineCount)}
          </div>
        )}
      </div>
    );
  }
  return (
    <div>
      <span
        style={{
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 700,
          background: "#f0fdf4",
          color: "#22c55e",
        }}
      >
        {t.statusHealthy}
      </span>
      {!compact && (
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
          {t.statusHealthyDetail}
        </div>
      )}
    </div>
  );
};

const BranchTableView = ({
  data = [],
  onRowClicked,
  onDelete = null,
  onEdit = null,
}) => {
  const { lang } = useProLanguage();
  const t = PRO_TRANSLATIONS[lang].branch;

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("list");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [hoveredRow, setHoveredRow] = useState(null);
  const HINT_KEY = "cdx_branch_click_hint_dismissed";
  const [showHint, setShowHint] = useState(
    () => typeof window !== "undefined" && !localStorage.getItem(HINT_KEY),
  );
  const dismissHint = () => {
    setShowHint(false);
    try {
      localStorage.setItem(HINT_KEY, "1");
    } catch (e) {
      /* ignore storage errors */
    }
  };
  const handleRowClick = (record) => {
    dismissHint();
    onRowClicked(record);
  };
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      if (sortDir === "asc") {
        setSortDir("desc");
      } else {
        // desc → clear (3rd click resets)
        setSortKey(null);
        setSortDir("asc");
      }
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  const SORT_FIELDS = {
    name: (b) => (b.BranchName || "").toLowerCase(),
    screens: (b) => b.ScreenCount || 0,
    online: (b) => b.OnlineCount || 0,
    offline: (b) => b.OfflineCount || 0,
    status: (b) => {
      if ((b.ScreenCount || 0) === 0) return 0;
      if ((b.OfflineCount || 0) > 0) return 1;
      return 2;
    },
  };

  const filtered = data.filter((b) =>
    b.BranchName.toLowerCase().includes(search.toLowerCase()),
  );

  const sorted = sortKey
    ? [...filtered].sort((a, b) => {
        const fn = SORT_FIELDS[sortKey];
        const va = fn(a);
        const vb = fn(b);
        if (va < vb) return sortDir === "asc" ? -1 : 1;
        if (va > vb) return sortDir === "asc" ? 1 : -1;
        return 0;
      })
    : filtered;

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageData = sorted.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const totalScreens = data.reduce((s, b) => s + (b.ScreenCount || 0), 0);
  const totalOnline = data.reduce((s, b) => s + (b.OnlineCount || 0), 0);
  const totalOffline = data.reduce((s, b) => s + (b.OfflineCount || 0), 0);

  const colStyle = (flex, extra = {}) => ({ flex, minWidth: 0, ...extra });

  const thStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  };

  const SortCarets = ({ field }) => {
    const active = sortKey === field;
    const up = active && sortDir === "asc";
    const down = active && sortDir === "desc";
    const active_color = "#6366f1";
    const inactive_color = "#cbd5e1";
    return (
      <span
        style={{
          display: "inline-flex",
          flexDirection: "column",
          marginLeft: 4,
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 0,
            height: 0,
            borderLeft: "3.5px solid transparent",
            borderRight: "3.5px solid transparent",
            borderBottom: `4px solid ${up ? active_color : inactive_color}`,
          }}
        />
        <span
          style={{
            width: 0,
            height: 0,
            borderLeft: "3.5px solid transparent",
            borderRight: "3.5px solid transparent",
            borderTop: `4px solid ${down ? active_color : inactive_color}`,
          }}
        />
      </span>
    );
  };

  const SortTh = ({ sortField, flex, children }) => {
    const active = sortKey === sortField;
    return (
      <div
        onClick={() => handleSort(sortField)}
        style={{
          ...colStyle(flex),
          ...thStyle,
          cursor: "pointer",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          color: active ? "#6366f1" : "#64748b",
        }}
      >
        {children}
        <SortCarets field={sortField} />
      </div>
    );
  };

  return (
    <motion.div
      variants={Animations.LIST_CONTAINER}
      initial="hidden"
      animate="show"
    >
      {showHint && filtered.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
            padding: "10px 14px",
            background: "#eef2ff",
            border: "1px solid #e0e7ff",
            borderRadius: 12,
          }}
        >
          <Info size={16} color="#6366f1" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: "#4338ca", fontWeight: 500 }}>
            {t.coachHint}
          </span>
          <button
            onClick={dismissHint}
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 4,
              border: "none",
              background: "transparent",
              color: "#6366f1",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              padding: "4px 6px",
            }}
          >
            {t.coachGotIt}
            <X size={14} />
          </button>
        </div>
      )}
      {/* Stats + search bar */}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          marginBottom: 16,
          gap: 12,
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}
      >
        {/* Left half — white, stats */}
        <div
          style={{
            flex: isMobile ? "1 1 100%" : "0 0 calc(50% - 6px)",
            background: "#fff",
            display: isMobile ? "grid" : "flex",
            gridTemplateColumns: isMobile ? "1fr 1fr" : undefined,
            gap: isMobile ? "12px 0" : undefined,
            alignItems: isMobile ? undefined : "center",
            flexDirection: isMobile ? undefined : "row",
            padding: "16px 24px",
            borderRadius: 16,
            border: "1px solid #f1f5f9",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          {isMobile ? (
            /* 2×2 grid on mobile */
            <>
              <div
                style={{ paddingRight: 24, borderRight: "1px solid #e2e8f0" }}
              >
                <StatCard label={t.statTotalLocations} value={data.length} />
              </div>
              <div style={{ paddingLeft: 24 }}>
                <StatCard label={t.statTotalScreens} value={totalScreens} />
              </div>
              <div
                style={{
                  paddingRight: 24,
                  borderRight: "1px solid #e2e8f0",
                  borderTop: "1px solid #f1f5f9",
                  paddingTop: 12,
                }}
              >
                <StatCard
                  label={t.statOnlineScreens}
                  value={totalOnline}
                  dot
                  dotColor="#22c55e"
                />
              </div>
              <div
                style={{
                  paddingLeft: 24,
                  borderTop: "1px solid #f1f5f9",
                  paddingTop: 12,
                }}
              >
                <StatCard
                  label={t.statOfflineScreens}
                  value={totalOffline}
                  dot
                  dotColor="#ef4444"
                />
              </div>
            </>
          ) : (
            /* Single row on desktop */
            <>
              <StatCard label={t.statTotalLocations} value={data.length} />
              <div
                style={{
                  width: 1,
                  height: 36,
                  background: "#e2e8f0",
                  flexShrink: 0,
                  margin: "0 24px",
                }}
              />
              <StatCard label={t.statTotalScreens} value={totalScreens} />
              <div
                style={{
                  width: 1,
                  height: 36,
                  background: "#e2e8f0",
                  flexShrink: 0,
                  margin: "0 24px",
                }}
              />
              <StatCard
                label={t.statOnlineScreens}
                value={totalOnline}
                dot
                dotColor="#22c55e"
              />
              <div
                style={{
                  width: 1,
                  height: 36,
                  background: "#e2e8f0",
                  flexShrink: 0,
                  margin: "0 24px",
                }}
              />
              <StatCard
                label={t.statOfflineScreens}
                value={totalOffline}
                dot
                dotColor="#ef4444"
              />
            </>
          )}
        </div>

        {/* Right half — main bg, search + toggle */}
        <div
          style={{
            flex: isMobile ? "1 1 100%" : "0 0 calc(50% - 6px)",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 12,
            padding: "16px 20px",
          }}
        >
          {/* Search */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: "7px 12px",
              flex: 1,
              maxWidth: 280,
            }}
          >
            <Search size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t.searchPlaceholder}
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 13,
                color: "#334155",
                width: "100%",
              }}
            />
          </div>
          {/* View toggle */}
          <div
            style={{
              display: "flex",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {[
              { key: "grid", Icon: LayoutGrid },
              { key: "list", Icon: List },
            ].map(({ key, Icon }) => (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                style={{
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: viewMode === key ? "#6366f1" : "#fff",
                  border: "none",
                  cursor: "pointer",
                  color: viewMode === key ? "#fff" : "#94a3b8",
                }}
              >
                <Icon size={15} />
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Grid / Table */}
      {viewMode === "grid" ? (
        /* ── Grid view ── */
        pageData.length === 0 ? (
          <EmptyState
            icon={<Monitor size={28} color="#6366f1" />}
            title={t.emptyTitle}
            description={t.emptyDesc}
          />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 16,
            }}
          >
            {pageData.map((record, index) => {
              const palette =
                ICON_PALETTE[data.indexOf(record) % ICON_PALETTE.length];
              const screenCount = record.ScreenCount ?? 0;
              const onlineCount = record.OnlineCount ?? 0;
              const offlineCount = record.OfflineCount ?? 0;
              const menuItems = [
                onEdit && {
                  key: "edit",
                  label: t.menuEdit,
                  onClick: () => onEdit(record),
                },
                onDelete && {
                  key: "delete",
                  label: (
                    <span style={{ color: "#ef4444" }}>{t.menuDelete}</span>
                  ),
                  onClick: () =>
                    Modal.confirm({
                      title: `Delete "${record.BranchName}"?`,
                      content: "This action cannot be undone.",
                      okText: "Delete",
                      okButtonProps: { danger: true },
                      cancelText: "Cancel",
                      centered: true,
                      onOk: () => onDelete(record),
                    }),
                },
              ].filter(Boolean);

              return (
                <motion.div
                  key={record.BranchID || index}
                  variants={Animations.LIST_ITEM}
                  onClick={() => handleRowClick(record)}
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    border: "1px solid #f1f5f9",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                    padding: "20px",
                    cursor: "pointer",
                    transition: "box-shadow 0.15s, transform 0.15s",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(99,102,241,0.12)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    setHoveredRow(index);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 1px 3px rgba(0,0,0,0.04)";
                    e.currentTarget.style.transform = "translateY(0)";
                    setHoveredRow(null);
                  }}
                >
                  {/* Actions menu */}
                  {menuItems.length > 0 && (
                    <div
                      style={{ position: "absolute", top: 12, right: 12 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Dropdown
                        menu={{ items: menuItems }}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <button
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            border: "1px solid #e2e8f0",
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "#94a3b8",
                          }}
                        >
                          <MoreVertical size={13} />
                        </button>
                      </Dropdown>
                    </div>
                  )}

                  {/* Icon + name */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      marginBottom: 16,
                      paddingRight: menuItems.length > 0 ? 32 : 0,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background: palette.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <MapPin size={20} color={palette.color} />
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#0f172a",
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                        paddingTop: 2,
                      }}
                    >
                      {record.BranchName}
                    </div>
                  </div>

                  {/* Stats row */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 8,
                      background: "#f8fafc",
                      borderRadius: 10,
                      padding: "10px 12px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: "#0f172a",
                        }}
                      >
                        {screenCount}
                      </div>
                      <div
                        style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}
                      >
                        {t.labelScreens(screenCount)}
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        borderLeft: "1px solid #e2e8f0",
                        borderRight: "1px solid #e2e8f0",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: "#22c55e",
                        }}
                      >
                        {onlineCount}
                      </div>
                      <div
                        style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}
                      >
                        {t.labelOnline}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: offlineCount > 0 ? "#f97316" : "#94a3b8",
                        }}
                      >
                        {offlineCount}
                      </div>
                      <div
                        style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}
                      >
                        {t.labelOffline}
                      </div>
                    </div>
                  </div>

                  {/* Status badge */}
                  <div
                    style={{
                      marginTop: 12,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <StatusBadge
                      offlineCount={offlineCount}
                      screenCount={screenCount}
                      t={t}
                    />
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: hoveredRow === index ? "#e0e7ff" : "#f1f5f9",
                        transition: "background 0.15s",
                      }}
                    >
                      <ChevronRight
                        size={16}
                        color={hoveredRow === index ? "#6366f1" : "#64748b"}
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )
      ) : (
        /* ── List / Table view ── */
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #f1f5f9",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          {/* Table header */}
          {!isMobile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px 20px",
                borderBottom: "1px solid #f1f5f9",
                gap: 16,
              }}
            >
              <SortTh sortField="name" flex="2 1 200px">
                {t.colLocation}
              </SortTh>
              <SortTh sortField="screens" flex="1 1 100px">
                {t.colScreens}
              </SortTh>
              <SortTh sortField="online" flex="1 1 80px">
                {t.colOnline}
              </SortTh>
              <SortTh sortField="offline" flex="1 1 80px">
                {t.colOffline}
              </SortTh>
              <SortTh sortField="status" flex="1 1 120px">
                {t.colStatus}
              </SortTh>
              <div style={{ width: 60, ...thStyle, textAlign: "right" }}>
                {t.colActions}
              </div>
              <div style={{ width: 28, flexShrink: 0 }} />
            </div>
          )}

          {/* Rows */}
          {pageData.length === 0 ? (
            <EmptyState
              icon={<Monitor size={28} color="#6366f1" />}
              title={t.emptyTitle}
              description={t.emptyDesc}
            />
          ) : (
            pageData.map((record, index) => {
              const palette =
                ICON_PALETTE[data.indexOf(record) % ICON_PALETTE.length];
              const screenCount = record.ScreenCount ?? 0;
              const onlineCount = record.OnlineCount ?? 0;
              const offlineCount = record.OfflineCount ?? 0;

              const menuItems = [
                onEdit && {
                  key: "edit",
                  label: t.menuEdit,
                  onClick: () => onEdit(record),
                },
                onDelete && {
                  key: "delete",
                  label: (
                    <span style={{ color: "#ef4444" }}>{t.menuDelete}</span>
                  ),
                  onClick: () =>
                    Modal.confirm({
                      title: `Delete "${record.BranchName}"?`,
                      content: "This action cannot be undone.",
                      okText: "Delete",
                      okButtonProps: { danger: true },
                      cancelText: "Cancel",
                      centered: true,
                      onOk: () => onDelete(record),
                    }),
                },
              ].filter(Boolean);

              const rowMenu = menuItems.length > 0 && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Dropdown
                    menu={{ items: menuItems }}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <button
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        background: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        color: "#64748b",
                        flexShrink: 0,
                      }}
                    >
                      <MoreVertical size={14} />
                    </button>
                  </Dropdown>
                </div>
              );

              const chevron = (
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: hoveredRow === index ? "#e0e7ff" : "#f1f5f9",
                    transform:
                      hoveredRow === index
                        ? "translateX(2px)"
                        : "translateX(0)",
                    transition: "background 0.15s, transform 0.15s",
                  }}
                >
                  <ChevronRight
                    size={16}
                    color={hoveredRow === index ? "#6366f1" : "#64748b"}
                  />
                </div>
              );

              if (isMobile) {
                return (
                  <motion.div
                    key={record.BranchID || index}
                    variants={Animations.LIST_ITEM}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      padding: "14px 16px",
                      gap: 10,
                      background:
                        hoveredRow === index
                          ? "#fafbff"
                          : index % 2 === 1
                            ? "#f8fafc"
                            : "transparent",
                      borderBottom:
                        index < pageData.length - 1
                          ? "1px solid #f8fafc"
                          : "none",
                      cursor: "pointer",
                    }}
                    onClick={() => handleRowClick(record)}
                  >
                    {/* Name row */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          background: palette.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <MapPin size={16} color={palette.color} />
                      </div>
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontWeight: 700,
                          fontSize: 14,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {record.BranchName}
                      </div>
                      {rowMenu}
                      {chevron}
                    </div>

                    {/* Compact stats row */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 14,
                        paddingLeft: 46,
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 5 }}
                        title={t.colScreens}
                      >
                        <Monitor size={13} color="#94a3b8" />
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#0f172a",
                          }}
                        >
                          {screenCount}
                        </span>
                      </div>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 5 }}
                        title={t.colOnline}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background:
                              onlineCount > 0 ? "#22c55e" : "#cbd5e1",
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: onlineCount > 0 ? "#22c55e" : "#94a3b8",
                          }}
                        >
                          {onlineCount}
                        </span>
                      </div>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 5 }}
                        title={t.colOffline}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background:
                              offlineCount > 0 ? "#f97316" : "#cbd5e1",
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: offlineCount > 0 ? "#f97316" : "#94a3b8",
                          }}
                        >
                          {offlineCount}
                        </span>
                      </div>
                      <StatusBadge
                        offlineCount={offlineCount}
                        screenCount={screenCount}
                        t={t}
                        compact
                      />
                    </div>
                  </motion.div>
                );
              }

              return (
                <motion.div
                  key={record.BranchID || index}
                  variants={Animations.LIST_ITEM}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px 20px",
                    gap: 16,
                    background:
                      hoveredRow === index
                        ? "#fafbff"
                        : index % 2 === 1
                          ? "#f8fafc"
                          : "transparent",
                    borderBottom:
                      index < pageData.length - 1
                        ? "1px solid #f8fafc"
                        : "none",
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => handleRowClick(record)}
                >
                  {/* Location */}
                  <div
                    style={{
                      ...colStyle("2 1 200px"),
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: palette.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <MapPin size={18} color={palette.color} />
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {record.BranchName}
                    </div>
                  </div>

                  {/* Screens */}
                  <div
                    style={{
                      ...colStyle("1 1 100px"),
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Monitor size={15} color="#94a3b8" />
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {screenCount}
                    </span>
                  </div>

                  {/* Online */}
                  <div
                    style={{
                      ...colStyle("1 1 80px"),
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: onlineCount > 0 ? "#22c55e" : "#cbd5e1",
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: onlineCount > 0 ? "#22c55e" : "#94a3b8",
                      }}
                    >
                      {onlineCount}
                    </span>
                  </div>

                  {/* Offline */}
                  <div
                    style={{
                      ...colStyle("1 1 80px"),
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: offlineCount > 0 ? "#f97316" : "#cbd5e1",
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: offlineCount > 0 ? "#f97316" : "#94a3b8",
                      }}
                    >
                      {offlineCount}
                    </span>
                  </div>

                  {/* Status */}
                  <div style={{ ...colStyle("1 1 120px") }}>
                    <StatusBadge
                      offlineCount={offlineCount}
                      screenCount={screenCount}
                      t={t}
                    />
                  </div>

                  {/* Actions */}
                  <div
                    style={{
                      width: 60,
                      display: "flex",
                      justifyContent: "flex-end",
                      flexShrink: 0
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {rowMenu}
                  </div>

                  {/* Drill-in affordance */}
                  {chevron}
                </motion.div>
              );
            })
          )}
        </div>
      )}
      {/* end list view */}
      {/* Pagination (both views) */}
      {filtered.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 4px",
            marginTop: 8,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {t.paginationShowing(
              Math.min((safePage - 1) * PAGE_SIZE + 1, filtered.length),
              Math.min(safePage * PAGE_SIZE, filtered.length),
              filtered.length,
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: "#fff",
                cursor: safePage === 1 ? "default" : "pointer",
                color: safePage === 1 ? "#cbd5e1" : "#64748b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
              }}
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border:
                    safePage === p
                      ? "1.5px solid #6366f1"
                      : "1px solid #e2e8f0",
                  background: safePage === p ? "#eef2ff" : "#fff",
                  color: safePage === p ? "#6366f1" : "#64748b",
                  fontWeight: safePage === p ? 700 : 400,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: "#fff",
                cursor: safePage === totalPages ? "default" : "pointer",
                color: safePage === totalPages ? "#cbd5e1" : "#64748b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
              }}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default BranchTableView;
